import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  PhotoCamera as PhotoCameraIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiService, {
  type AdminBrand,
  type AdminBrandPatch,
  type AdminBrandTranscriptFingerprint,
} from "../services/api";
import BrandStringListEditor from "../components/BrandStringListEditor";
import {
  BRAND_CATEGORIES,
  BRAND_CATEGORY_LABELS,
} from "../constants/brandCategories";
import { getProgramImageUrl } from "../utils/imageUtils";

const BRAND_TYPES = [
  { value: "product", label: "Producto" },
  { value: "service", label: "Servicio" },
  { value: "company", label: "Empresa" },
  { value: "platform", label: "Plataforma" },
  { value: "other", label: "Otro" },
] as const;

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("No se pudo leer la imagen"));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Read failed"));
    reader.readAsDataURL(file);
  });

export const adminBrandLogoUrl = (
  brand: Pick<AdminBrand, "s3Key" | "logoUrl" | "imageUpdatedAt"> | null,
): string => {
  if (!brand) return "";
  if (brand.s3Key) {
    return getProgramImageUrl({
      imageS3Key: brand.s3Key,
      imageUpdatedAt: brand.imageUpdatedAt,
    });
  }
  return brand.logoUrl ?? "";
};

type BrandAnalysisDrawerProps = {
  brandId: string | null;
  brandSummary: AdminBrand | null;
  onClose: () => void;
};

type FormState = {
  name: string;
  slug: string;
  category: string;
  subcategory: string;
  brandType: string;
  companyName: string;
  website: string;
  country: string;
  description: string;
  context: string;
  primaryColor: string;
  sponsorshipNotes: string;
  notes: string;
  aliases: string[];
  antiAliases: string[];
  overlapSuppressAliases: string[];
  socialAccounts: string[];
  keywords: string[];
  industryTags: string[];
  isActive: boolean;
  canSponsor: boolean;
  isVerified: boolean;
};

const emptyForm = (): FormState => ({
  name: "",
  slug: "",
  category: "",
  subcategory: "",
  brandType: "",
  companyName: "",
  website: "",
  country: "",
  description: "",
  context: "",
  primaryColor: "",
  sponsorshipNotes: "",
  notes: "",
  aliases: [],
  antiAliases: [],
  overlapSuppressAliases: [],
  socialAccounts: [],
  keywords: [],
  industryTags: [],
  isActive: true,
  canSponsor: true,
  isVerified: false,
});

const formFromBrand = (brand: AdminBrand): FormState => ({
  name: brand.name ?? "",
  slug: brand.slug ?? "",
  category: brand.category ?? "",
  subcategory: brand.subcategory ?? "",
  brandType: brand.brandType ?? "",
  companyName: brand.companyName ?? "",
  website: brand.website ?? "",
  country: brand.country ?? "",
  description: brand.description ?? "",
  context: brand.context ?? "",
  primaryColor: brand.primaryColor ?? "",
  sponsorshipNotes: brand.sponsorshipNotes ?? "",
  notes: brand.notes ?? "",
  aliases: brand.aliases ?? [],
  antiAliases: brand.antiAliases ?? [],
  overlapSuppressAliases: brand.overlapSuppressAliases ?? [],
  socialAccounts: brand.socialAccounts ?? [],
  keywords: brand.keywords ?? [],
  industryTags: brand.industryTags ?? [],
  isActive: brand.isActive !== false,
  canSponsor: brand.canSponsor !== false,
  isVerified: brand.isVerified === true,
});

const nullable = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const BrandAnalysisDrawer: React.FC<BrandAnalysisDrawerProps> = ({
  brandId,
  brandSummary,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [imageUrl, setImageUrl] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [fingerprintDraft, setFingerprintDraft] = useState("");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const open = Boolean(brandId);

  const detailQuery = useQuery({
    queryKey: ["admin-brand", brandId],
    queryFn: () => apiService.getAdminBrand(brandId!),
    enabled: open,
  });

  const brand = detailQuery.data?.brand ?? brandSummary;

  useEffect(() => {
    if (!open) {
      setForm(emptyForm());
      setImageUrl("");
      setConfirmDelete(false);
      setFingerprintDraft("");
      return;
    }
    if (brand) setForm(formFromBrand(brand));
    // Fingerprint saves patch the cached brand without changing these fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, brand?._id, brand?.updatedAt]);

  const saveMutation = useMutation({
    mutationFn: (payload: AdminBrandPatch) =>
      apiService.patchAdminBrand(brandId!, payload),
    onSuccess: (result) => {
      queryClient.setQueryData(["admin-brand", brandId], result);
      void queryClient.invalidateQueries({ queryKey: ["admin-brands"] });
      setForm(formFromBrand(result.brand));
      setSnackbar({
        open: true,
        message: "Marca actualizada",
        severity: "success",
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: "No se pudo guardar la marca",
        severity: "error",
      });
    },
  });

  const applyLogoResult = (result: { brand: AdminBrand }, message: string) => {
    queryClient.setQueryData(["admin-brand", brandId], result);
    void queryClient.invalidateQueries({ queryKey: ["admin-brands"] });
    setSnackbar({ open: true, message, severity: "success" });
  };

  const logoMutation = useMutation({
    mutationFn: async (file: File) => {
      const imageBase64 = await fileToBase64(file);
      return apiService.uploadAdminBrandLogo(brandId!, {
        imageBase64,
        contentType: file.type || "image/png",
        fileName: file.name,
      });
    },
    onSuccess: (result) => applyLogoResult(result, "Imagen actualizada"),
    onError: () => {
      setSnackbar({
        open: true,
        message: "No se pudo subir la imagen",
        severity: "error",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiService.deleteAdminBrand(brandId!),
    onSuccess: (result) => {
      setConfirmDelete(false);
      void queryClient.invalidateQueries({ queryKey: ["admin-brands"] });
      void queryClient.removeQueries({ queryKey: ["admin-brand", brandId] });
      setSnackbar({
        open: true,
        message:
          result.mentionsDeleted > 0
            ? `Marca eliminada (${result.mentionsDeleted} menciones)`
            : "Marca eliminada",
        severity: "success",
      });
      onClose();
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: "No se pudo eliminar la marca",
        severity: "error",
      });
    },
  });

  const logoUrlMutation = useMutation({
    mutationFn: (url: string) =>
      apiService.uploadAdminBrandLogo(brandId!, { imageUrl: url }),
    onSuccess: (result) => {
      applyLogoResult(result, "Imagen importada (PNG 250×250)");
      setImageUrl("");
    },
    onError: (error: unknown) => {
      const apiMessage =
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "error" in error.response.data &&
        typeof (error.response.data as { error?: unknown }).error === "string"
          ? (error.response.data as { error: string }).error
          : "No se pudo importar la imagen";
      setSnackbar({
        open: true,
        message: apiMessage,
        severity: "error",
      });
    },
  });

  const applyFingerprints = (next: AdminBrandTranscriptFingerprint[]) => {
    queryClient.setQueryData(
      ["admin-brand", brandId],
      (prev: { brand: AdminBrand } | undefined) =>
        prev?.brand
          ? { ...prev, brand: { ...prev.brand, transcriptFingerprints: next } }
          : prev,
    );
  };

  const addFingerprintMutation = useMutation({
    mutationFn: (text: string) =>
      apiService.addBrandTranscriptFingerprint(brandId!, text),
    onSuccess: (next) => {
      applyFingerprints(next);
      setFingerprintDraft("");
    },
  });

  const removeFingerprintMutation = useMutation({
    mutationFn: (mentionId: string) =>
      apiService.removeBrandTranscriptFingerprint(brandId!, mentionId),
    onSuccess: (next) => applyFingerprints(next),
  });

  const handleSave = () => {
    if (!form.name.trim()) {
      setSnackbar({
        open: true,
        message: "El nombre es obligatorio",
        severity: "error",
      });
      return;
    }
    saveMutation.mutate({
      name: form.name.trim(),
      slug: nullable(form.slug),
      category: nullable(form.category),
      subcategory: nullable(form.subcategory),
      brandType: nullable(form.brandType),
      companyName: nullable(form.companyName),
      website: nullable(form.website),
      country: nullable(form.country),
      description: nullable(form.description),
      context: nullable(form.context),
      primaryColor: nullable(form.primaryColor),
      sponsorshipNotes: nullable(form.sponsorshipNotes),
      notes: nullable(form.notes),
      aliases: form.aliases,
      antiAliases: form.antiAliases,
      overlapSuppressAliases: form.overlapSuppressAliases,
      socialAccounts: form.socialAccounts,
      keywords: form.keywords,
      industryTags: form.industryTags,
      isActive: form.isActive,
      canSponsor: form.canSponsor,
      isVerified: form.isVerified,
    });
  };

  const handleImportFromUrl = () => {
    const url = imageUrl.trim();
    if (!url) {
      setSnackbar({
        open: true,
        message: "Pega una URL de imagen",
        severity: "error",
      });
      return;
    }
    logoUrlMutation.mutate(url);
  };

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setSnackbar({
        open: true,
        message: "El archivo debe ser una imagen",
        severity: "error",
      });
      return;
    }
    logoMutation.mutate(file);
  };

  const categoryOptions = [...BRAND_CATEGORIES];
  if (
    form.category &&
    form.category !== "unknown" &&
    !categoryOptions.includes(form.category)
  ) {
    categoryOptions.unshift(form.category);
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 560 },
            bgcolor: "background.default",
          },
        }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            p: 2.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" flex={1}>
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={adminBrandLogoUrl(brand)}
                alt={brand?.name ?? ""}
                sx={{ width: 56, height: 56 }}
              >
                {brand?.name?.charAt(0) ?? "?"}
              </Avatar>
              <IconButton
                size="small"
                onClick={() => fileInputRef.current?.click()}
                disabled={logoMutation.isPending || logoUrlMutation.isPending}
                sx={{
                  position: "absolute",
                  right: -6,
                  bottom: -6,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": { bgcolor: "background.paper" },
                }}
              >
                {logoMutation.isPending || logoUrlMutation.isPending ? (
                  <CircularProgress size={16} />
                ) : (
                  <PhotoCameraIcon fontSize="small" />
                )}
              </IconButton>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                onChange={(e) => {
                  handleFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </Box>
            <Box minWidth={0}>
              <Typography variant="h6" fontWeight={600} noWrap>
                {brand?.name ?? "Marca"}
              </Typography>
              {brand?.slug && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {brand.slug}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2.5, overflow: "auto" }}>
          {detailQuery.isLoading && !brand ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : detailQuery.isError && !brand ? (
            <Alert severity="error">No se pudo cargar la marca.</Alert>
          ) : (
            <Stack spacing={2.5}>
              <Typography variant="subtitle2" color="text.secondary">
                Imagen
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Se guarda como PNG 250×250 (máxima calidad desde el original).
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  label="URL de imagen"
                  size="small"
                  fullWidth
                  placeholder="https://…"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleImportFromUrl();
                    }
                  }}
                  disabled={logoUrlMutation.isPending}
                />
                <Button
                  variant="outlined"
                  onClick={handleImportFromUrl}
                  disabled={logoUrlMutation.isPending || !imageUrl.trim()}
                  sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                >
                  {logoUrlMutation.isPending ? "Importando…" : "Importar URL"}
                </Button>
              </Stack>
              <Divider />
              <Typography variant="subtitle2" color="text.secondary">
                Identidad
              </Typography>
              <TextField
                label="Nombre"
                size="small"
                fullWidth
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />
              <TextField
                label="Slug"
                size="small"
                fullWidth
                value={form.slug}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, slug: e.target.value }))
                }
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Categoría</InputLabel>
                  <Select
                    label="Categoría"
                    value={form.category}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, category: e.target.value }))
                    }
                  >
                    <MenuItem value="">Sin categoría</MenuItem>
                    <MenuItem value="unknown">Unknown</MenuItem>
                    {categoryOptions.map((key) => (
                      <MenuItem key={key} value={key}>
                        {BRAND_CATEGORY_LABELS[key] ?? key}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    label="Tipo"
                    value={form.brandType}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        brandType: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">—</MenuItem>
                    {BRAND_TYPES.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <TextField
                label="Subcategoría"
                size="small"
                fullWidth
                value={form.subcategory}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, subcategory: e.target.value }))
                }
              />
              <TextField
                label="Empresa"
                size="small"
                fullWidth
                value={form.companyName}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, companyName: e.target.value }))
                }
              />
              <TextField
                label="Sitio web"
                size="small"
                fullWidth
                value={form.website}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, website: e.target.value }))
                }
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="País"
                  size="small"
                  fullWidth
                  value={form.country}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, country: e.target.value }))
                  }
                />
                <TextField
                  label="Color primario"
                  size="small"
                  fullWidth
                  value={form.primaryColor}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      primaryColor: e.target.value,
                    }))
                  }
                />
              </Stack>
              <TextField
                label="Descripción"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={form.description}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, description: e.target.value }))
                }
              />
              <TextField
                label="Contexto (hint de producto)"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={form.context}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, context: e.target.value }))
                }
              />

              <Divider />
              <Typography variant="subtitle2" color="text.secondary">
                Matching
              </Typography>
              <BrandStringListEditor
                items={form.aliases}
                onChange={(aliases) => setForm((prev) => ({ ...prev, aliases }))}
                addLabel="Alias"
                emptyMessage="Sin aliases"
                listLabel="Aliases"
              />
              <BrandStringListEditor
                items={form.antiAliases}
                onChange={(antiAliases) =>
                  setForm((prev) => ({ ...prev, antiAliases }))
                }
                addLabel="Anti-alias"
                emptyMessage="Sin anti-aliases"
                listLabel="Anti-aliases"
              />
              <BrandStringListEditor
                items={form.overlapSuppressAliases}
                onChange={(overlapSuppressAliases) =>
                  setForm((prev) => ({ ...prev, overlapSuppressAliases }))
                }
                addLabel="Overlap suppress"
                emptyMessage="Sin overlap suppress"
                listLabel="Overlap suppress aliases"
              />
              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Transcript fingerprints
                </Typography>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    label="Lectura del auspicio"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={fingerprintDraft}
                    onChange={(e) => setFingerprintDraft(e.target.value)}
                    helperText="Al menos 6 palabras. Las líneas que repitan esta lectura se etiquetan como esta marca."
                    disabled={addFingerprintMutation.isPending}
                  />
                  <Button
                    variant="contained"
                    startIcon={
                      addFingerprintMutation.isPending ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <AddIcon />
                      )
                    }
                    onClick={() =>
                      addFingerprintMutation.mutate(fingerprintDraft)
                    }
                    disabled={
                      addFingerprintMutation.isPending ||
                      !fingerprintDraft.trim()
                    }
                    sx={{ flexShrink: 0, mt: 0.5 }}
                  >
                    Add
                  </Button>
                </Stack>
                {addFingerprintMutation.error ? (
                  <Alert severity="error">
                    {(addFingerprintMutation.error as Error).message}
                  </Alert>
                ) : null}
                {detailQuery.isLoading ? (
                  <CircularProgress size={16} />
                ) : (brand?.transcriptFingerprints ?? []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Sin transcript fingerprints
                  </Typography>
                ) : (
                  (brand?.transcriptFingerprints ?? []).map((fingerprint) => (
                    <Stack
                      key={fingerprint.mentionId || fingerprint.text}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                          “{fingerprint.text}”
                        </Typography>
                        {fingerprint.mentionId && !fingerprint.manual ? (
                          <Typography variant="caption" color="text.secondary">
                            mention {fingerprint.mentionId}
                          </Typography>
                        ) : null}
                      </Box>
                      <Button
                        size="small"
                        color="error"
                        disabled={
                          removeFingerprintMutation.isPending ||
                          !fingerprint.mentionId
                        }
                        onClick={() =>
                          removeFingerprintMutation.mutate(fingerprint.mentionId)
                        }
                      >
                        Quitar
                      </Button>
                    </Stack>
                  ))
                )}
                {removeFingerprintMutation.error ? (
                  <Alert severity="error">
                    {(removeFingerprintMutation.error as Error).message}
                  </Alert>
                ) : null}
              </Stack>
              <BrandStringListEditor
                items={form.keywords}
                onChange={(keywords) =>
                  setForm((prev) => ({ ...prev, keywords }))
                }
                addLabel="Keyword"
                emptyMessage="Sin keywords"
                listLabel="Keywords"
              />
              <BrandStringListEditor
                items={form.industryTags}
                onChange={(industryTags) =>
                  setForm((prev) => ({ ...prev, industryTags }))
                }
                addLabel="Industry tag"
                emptyMessage="Sin industry tags"
                listLabel="Industry tags"
              />
              <BrandStringListEditor
                items={form.socialAccounts}
                onChange={(socialAccounts) =>
                  setForm((prev) => ({ ...prev, socialAccounts }))
                }
                addLabel="Cuenta social"
                emptyMessage="Sin cuentas sociales"
                listLabel="Cuentas sociales"
              />

              <Divider />
              <Typography variant="subtitle2" color="text.secondary">
                Estado
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isActive}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          isActive: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Activa"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.canSponsor}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          canSponsor: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Puede auspiciar"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isVerified}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          isVerified: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Verificada"
                />
              </Stack>
              <TextField
                label="Notas de auspicio"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={form.sponsorshipNotes}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    sponsorshipNotes: e.target.value,
                  }))
                }
              />
              <TextField
                label="Notas internas"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={form.notes}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, notes: e.target.value }))
                }
              />

              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saveMutation.isPending || deleteMutation.isPending}
              >
                {saveMutation.isPending ? "Guardando…" : "Guardar cambios"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setConfirmDelete(true)}
                disabled={saveMutation.isPending || deleteMutation.isPending}
              >
                Eliminar marca
              </Button>
            </Stack>
          )}
        </Box>
      </Drawer>

      <Dialog
        open={confirmDelete}
        onClose={() => !deleteMutation.isPending && setConfirmDelete(false)}
      >
        <DialogTitle>Eliminar marca</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Eliminar {brand?.name?.trim() || "esta marca"}? Esta acción no se
            puede deshacer. Las menciones asociadas también se eliminarán.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDelete(false)}
            disabled={deleteMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "Eliminando…" : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BrandAnalysisDrawer;
